import { GridActionsCellItem } from '@mui/x-data-grid';
import { Paper, IconButton, Grid, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
} from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import subscriptionService from './subscriptionService';
import DataList from '../../components/DataList';

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { canPlatform } = useCheckPermission();

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    subscriptionService
      .list()
      .then((response) => {
        if (!response.hasError) setRows(response.records);
        else
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const getActions = (params: GridRowParams) => {
    const actions: any[] = [];
    const readonly = !canPlatform(['ManageSubscriptions']);
    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="View">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'View' : 'Edit'}
        onClick={() => navigate(`/subscriptions/edit/${params.row.id}`)}
      />
    );

    if (canPlatform(['ManageSubscriptions'])) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Delete">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() =>
            setParams({
              open: true,
              name: 'Are you sure to delete this item?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    { field: 'name', headerName: 'Name', flex: 100 },
    {
      field: 'tenant',
      headerName: 'Bérlő',
      flex: 100,
      valueGetter: (_value: any, row: any) => row.tenant?.name ?? row.tenantId,
    },
    {
      field: 'license',
      headerName: 'License',
      flex: 100,
      valueGetter: (_value: any, row: any) => row.license?.name ?? row.licenseId,
    },
    { field: 'status', headerName: 'Status', flex: 60 },
    { field: 'paymentOccurrence', headerName: 'Payment', flex: 80 },
    {
      field: 'actions',
      type: 'actions',
      flex: 30,
      getActions,
    },
  ];

  const handleDelete = (id: string) => {
    subscriptionService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar((response.errorMessages ?? []).join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Successful delete', { variant: 'success' });
        setRows((prev) => prev.filter((row) => row.id !== id));
      }
    });
  };

  return (
    <Paper>
      <Grid container sx={{ p: 3 }}>
        <Grid size={12}>
          <h2>Subscriptions</h2>
        </Grid>
        {canPlatform(['ManageSubscriptions']) && (
          <Grid container size={12} sx={{ p: 2, justifyContent: 'end' }}>
            <Grid>
              <Tooltip title="Create Subscription">
                <IconButton
                  component={RouterLink}
                  to={`/subscriptions/create`}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList rows={rows} columns={columns} />
        </Grid>
        <ConfirmDeleteDialog />
      </Grid>
    </Paper>
  );
};

export default SubscriptionsPage;
